import { ExternalLink, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { StatusBadge, ConfidenceBadge } from "@/components/Bits";

function Row({ k, v, testid }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 py-2 text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="break-words" data-testid={testid}>{v || "—"}</span>
    </div>
  );
}

const link = (u) => u ? <a href={u} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">{u.replace(/^https?:\/\//, "").slice(0, 48)} <ExternalLink className="h-3 w-3" /></a> : null;

export default function LeadSheet({ lead, onClose, onAction, onChange, aiOn }) {
  const [email, setEmail] = useState("");
  const [src, setSrc] = useState("");
  const [busy, setBusy] = useState(false);
  if (!lead) return null;

  const fixEmail = async () => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/email`, { email, source_url: src });
      onChange(data); setEmail(""); setSrc("");
      toast.success(`Email saved (${data.confidence} confidence)`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const summarize = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/leads/${lead.id}/ai-summary`); onChange({ ...lead, ai_summary: data.ai_summary }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="lead-sheet">
        <SheetHeader>
          <div className="kh-label">Lead · score {lead.score}</div>
          <SheetTitle className="font-display text-2xl" data-testid="lead-sheet-name">{lead.name}</SheetTitle>
          <div className="flex gap-2"><StatusBadge status={lead.status} /><ConfidenceBadge level={lead.confidence} /></div>
        </SheetHeader>
        <div className="mt-6 divide-y">
          <Row k="Website" v={link(lead.website)} />
          <Row k="Source" v={<span>{lead.source} · {link(lead.source_url)}</span>} />
          <Row k="Contact" v={lead.contact_name} testid="lead-sheet-contact" />
          <Row k="Email" v={lead.email} testid="lead-sheet-email" />
          <Row k="Email source" v={link(lead.email_source_url)} />
          <Row k="Phone" v={lead.phone} />
          <Row k="Location" v={[lead.city, lead.province, lead.country].filter(Boolean).join(", ")} />
          {lead.youtube && <Row k="YouTube" v={`${lead.youtube.subscribers.toLocaleString()} subs · ${lead.youtube.videos} videos`} />}
          {lead.audit && <Row k="Audit" v={lead.audit.ok ? `${lead.audit.load_s}s load · ${lead.audit.size_kb} KB` : `Site not reachable (${lead.audit.error || "n/a"})`} />}
        </div>
        <div className="mt-6">
          <div className="kh-label mb-3">Qualification evidence</div>
          {lead.signals.length === 0 && <div className="text-sm text-muted-foreground">No signals triggered.</div>}
          <div className="space-y-2">
            {lead.signals.map((s) => (
              <div key={s.key} className="rounded-lg border bg-background/50 p-3" data-testid={`lead-signal-${s.key}`}>
                <div className="flex justify-between text-sm"><span className="font-medium">{s.label}</span><span className="font-mono text-xs text-primary">+{s.weight}</span></div>
                <div className="mt-1 text-xs text-muted-foreground">{s.evidence}</div>
              </div>
            ))}
          </div>
        </div>
        {(lead.ai_summary || aiOn) && (
          <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4">
            <div className="flex items-center justify-between"><div className="kh-label">AI summary (advisory)</div>
              {aiOn && <Button size="sm" variant="ghost" onClick={summarize} disabled={busy} data-testid="lead-ai-summary-btn"><Sparkles className="mr-1 h-3.5 w-3.5" />{busy ? "Thinking…" : "Summarize"}</Button>}
            </div>
            {lead.ai_summary && <p className="mt-2 text-sm leading-relaxed" data-testid="lead-ai-summary">{lead.ai_summary}</p>}
          </div>
        )}
        <div className="mt-6 rounded-xl border p-4">
          <div className="kh-label mb-2">Add email found on a public page</div>
          <div className="space-y-2">
            <Input placeholder="name@business.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="lead-email-input" />
            <Input placeholder="Page URL where it's listed" value={src} onChange={(e) => setSrc(e.target.value)} data-testid="lead-email-source-input" />
            <Button size="sm" variant="secondary" disabled={!email || !src} onClick={fixEmail} data-testid="lead-email-save-btn">Save email</Button>
          </div>
        </div>
        <div className="sticky bottom-0 mt-6 flex flex-wrap gap-2 border-t bg-card py-4">
          <Button onClick={() => onAction(lead, "approve")} data-testid="sheet-approve-btn">Approve for draft</Button>
          <Button variant="secondary" onClick={() => onAction(lead, "save")} data-testid="sheet-save-btn">Save for later</Button>
          <Button variant="ghost" onClick={() => onAction(lead, "skip")} data-testid="sheet-skip-btn">Skip</Button>
          <Button variant="ghost" className="text-red-700" onClick={() => onAction(lead, "blacklist")} data-testid="sheet-blacklist-btn">Blacklist</Button>
        </div>
        <div className="pb-4 text-xs text-muted-foreground">
          History: {(lead.history || []).map((h) => `${h.status} (${h.by}, ${fmtDateTime(h.at)})`).join(" → ")}
        </div>
      </SheetContent>
    </Sheet>
  );
}
